import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useLocation } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { MI_MEETINGS_TOUR, handleCheckStepEnabled } from 'features/meeting_intelligence'
import type { Meeting } from 'features/meetings/types'
import { useScope } from 'hooks/meetings/use_scope'

import {
  getMeetingCardHostDisplayName,
  getParticipantText,
  isExternalCalendarSource,
} from '../../utils'

/**
 * Derives display data for the `MeetingSummary` component.
 *
 * Card rows use search/supergraph-shaped meetings ({@link mapNodeToMeeting}): `host` is set
 * from the HOST attendee. Collective / round-robin names use {@link getMeetingCardHostDisplayName}
 * for participant copy. Subtitle text comes from `getParticipantText` (solo invitees, group counts,
 * “hosted by” by scope).
 *
 * @param event - The meeting to derive summary data from
 */
export const useMeetingSummaryData = (event: Meeting) => {
  const {
    cancelled,
    eventType: { kind } = {},
    source,
    ownerUserUuid,
    invitees,
    invitee,
    host,
    filledCount,
    eventMemberships,
  } = event
  const isGroup = kind === 'group'

  const { data: currentUser } = useGetUserQuery(undefined)
  const { scopeType, scopeId } = useScope()
  const location = useLocation()
  const isPhone = useDeviceType('isPhone')
  const showStepThree = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_THREE)

  const isExternalMeeting = isExternalCalendarSource(source)
  const isHostMeeting = isExternalMeeting
    ? host?.email === currentUser?.email
    : ownerUserUuid === currentUser?.uuid

  const hostDisplayName = getMeetingCardHostDisplayName(event)
  const hostForCopy = hostDisplayName != null
    ? { name: hostDisplayName }
    : host ?? eventMemberships?.[0]

  let soloInvitees = invitees ?? []
  if (!soloInvitees.length && invitee) {
    soloInvitees = [invitee]
  }
  const cardInvitees = isGroup ? (invitees ?? []) : soloInvitees

  const participantText = getParticipantText({
    isHostMeeting,
    source,
    invitees: cardInvitees,
    host: hostForCopy,
    scopeType: scopeType ?? undefined,
    scopeId: scopeId ?? undefined,
    isGroup,
    filledCount,
  }) ?? null

  return {
    cancelled,
    isGroup,
    participantText,
    showStepThree,
    isPhone,
  }
}
